import { Fragment } from 'react'
import { GateShell } from './GateShell'
import type { GateAnswer, GateOption } from './GateShell'
import type { Frame } from '../../stream/events'
import type { GateAnswered } from '../../stream/reducer'
import { agentFor } from '../../phases'
import { ClarifyCard } from './ClarifyCard'

interface GateCardProps {
  frame: Frame
  busy?: boolean
  onAnswer: (answer: GateAnswer) => void

  /** The decision, once a person has made one. An answered card stays, inert (`AC-UI-8`). */
  answer?: GateAnswered | null

}

/**
 * ONE SENTENCE SAYING WHAT IS BEING DECIDED, per kind. `Frontend/engagement.html` heads every card
 * with one, and a card that opened straight into evidence asks the reader to work out the question
 * from the answer.
 */
const HEADINGS: Record<string, string> = {
  clarify: 'A few things I need settled before I can size this',
  approve_binding: 'A value, and what is wrong with it',
  capability: 'A decision was needed before I continued',
  ratify: 'Approve the approach, and its stated limitations',
  geography: 'Values measured in another economy',
  suppression: 'Two methods claim the same effect',
  conversion: 'A unit conversion on one edge',
  framing: 'Two different questions',
  assumption_override: 'Assumptions this analysis does not satisfy',
  sign: 'Sign and release',
}

/**
 * TEN DISTINCT RENDERINGS. `04 §5`.
 *
 * <p>"A single generic text box discards the structure the agent supplied." Every card below exists
 * because its payload carries something a generic renderer would flatten into prose — the direction
 * of error, the rejected alternatives with their disqualifying conditions, the conversion's factor,
 * the Green Book text verbatim.</p>
 *
 * <p><b>THE CLIENT RENDERS; IT DOES NOT COMPUTE.</b> Every card here is a pure projection of the
 * payload the harness built from committed keys (`02 §4.1`). Where a field is absent the card says
 * so rather than deriving it — a card that computed a missing consequence would be showing the
 * reader something nobody committed.</p>
 */
export function GateCard({ frame, busy, onAnswer, answer = null }: GateCardProps)
  : React.JSX.Element {
  const payload = (frame['payload'] ?? frame) as Record<string, unknown>
  const kind = str(frame['gate']) ?? str(frame['gateKind']) ?? 'gate'
  const origin = str(frame['origin']) === 'agent' ? 'agent' : 'harness'
  const state = str(frame['state']) ?? 'Awaiting you'

  const shell = (body: React.ReactNode, options: readonly GateOption[],
                 heading: string | undefined = HEADINGS[kind]) => (
    <GateShell kind={kind} origin={origin} state={state} options={options}
               busy={busy ?? false} onAnswer={onAnswer} answer={answer}
               from={agentFor(num(frame['phase'])).name}
               {...(heading !== undefined ? { heading } : {})}>
      {body}
    </GateShell>
  )

  switch (kind) {
    case 'clarify':
      // ITS OWN COMPONENT, because `§5.1` gives it a shape no other card has: ONE ANSWER PER
      // QUESTION, a free-text field on every one of them always, and a "Reply instead…" that sets
      // `response` rather than `answers`. The shell's single verdict cannot express any of that.
      return (
        <ClarifyCard payload={payload} busy={busy ?? false} answer={answer}
                     onAnswer={onAnswer} from={agentFor(num(frame['phase'])).name}
                     options={declared(payload, [
                       opt('answered', 'Answer',
                           'Your answers are recorded verbatim and the analysis continues.'),
                     ])} />
      )

    case 'approve_binding':
      return (
        <BindingCard payload={payload} frame={frame} busy={busy ?? false} answer={answer}
                     onAnswer={onAnswer} options={declared(payload, [
                       opt('approve', 'Approve this value',
                           'It is bound, with its grade and every weakness stated below.'),
                       opt('supply', 'Supply my own…',
                           'You give the value instead, recorded verbatim.'),
                       opt('reject', 'Reject', 'The analyst is asked for a different value.'),
                     ])}
                     />
      )

    case 'capability':
      return shell(<Capability payload={payload} />, declared(payload, [
        opt('proceed', 'Proceed', 'The analysis runs within the boundary described.'),
        opt('commission_cross_check', 'Commission a cross-check',
            'Recorded as a request; this system cannot run the alternative method itself.'),
        opt('stop', 'Stop', 'The engagement ends here, with the reason on the record.'),
      ]))

    case 'ratify':
      return shell(<Ratify payload={payload} />, declared(payload, [
        opt('approve', 'Approve the approach', 'Method selection is settled and the run continues.'),
        opt('reject', 'Reject', 'The engagement ends here.'),
        opt('redirect', 'Send it back', 'Method selection is reopened with your note as the brief.'),
      ]))

    case 'geography':
      return shell(<Geography payload={payload} />, declared(payload, [
        opt('accept_with_caveat', 'Accept, with the transfer stated',
            'Each parameter below is used AND enters the sensitivity scope.'),
        opt('override', 'Override', 'You supply a local value instead.'),
        opt('stop', 'Stop', 'The engagement ends here.'),
      ]))

    case 'suppression':
      return shell(<Suppression payload={payload} />, declared(payload, [
        opt('suppress', 'Suppress the duplicate',
            'One of the two effects is excluded, on the registered grounds shown.'),
        opt('intentional', 'Both are intended',
            'Both effects stay. The figure counts this activity twice, on purpose.'),
        opt('error', 'This is an error', 'The engagement ends here so the composition can be fixed.'),
      ]))

    case 'conversion':
      // `AC-UI-18`: ONLY FOR A CONVERSION THE USER MUST CONFIRM. An `automatic` conversion is
      // applied by the harness on the strength of the registry and produces no card — putting one
      // on screen would ask a person to approve something that was never theirs to approve, and
      // teach them that approving conversions is a formality.
      if (str(payload['approval']) === 'automatic') return <></>

      return shell(<Conversion payload={payload} />, declared(payload, [
        opt('confirm', 'Confirm the conversion', 'The factor below is applied on that edge.'),
        opt('reject', 'Reject', 'The engagement ends here rather than converting silently.'),
      ]))

    case 'framing': {
      // AC-UI-14. The Green Book block is sent only when the discounting overlay was accepted
      // (B-222); without it the gate was raised for the horizon conflict alone, and the card is
      // about that and nothing else.
      const greenBook = (str(payload['text']) ?? '') !== ''
      return shell(<Framing payload={payload} />, declared(payload, [
        greenBook
          ? opt('proceed', 'Proceed under the impact framing',
                'Stated beside the figures in the report.')
          : opt('proceed', 'Proceed with the stated horizon',
                'The report states the horizon used and the standard it diverges from.'),
        opt('stop', 'Stop', 'The engagement ends here.'),
      ]), greenBook ? HEADINGS['framing'] : 'Two different horizons')
    }

    case 'assumption_override': {
      // `AC-UI-19`: A NON-OVERRIDABLE VIOLATION RENDERS NO CARD. Offering *Override* on a
      // violation the harness will refuse to override puts a button on screen that answers
      // nothing, and the refusal then arrives as though the person had done something wrong.
      const violations = array(payload['violations']);
      if (violations.length > 0 && violations.every((v) => v['overridable'] === false)) {
        return <></>
      }

      return (
        <GateShell kind={kind} origin={origin} state={state} busy={busy ?? false}
                   onAnswer={onAnswer} answer={answer} from={str(frame['specialist']) ?? 'analyst'}
                   heading={HEADINGS[kind] ?? ''}
                   options={declared(payload, [
                     opt('override', 'Override, and record why',
                         'The violation stands, acknowledged, and appears in the report.'),
                     opt('stop', 'Stop', 'The engagement ends here.'),
                   ])}
                   acknowledge={{
                     option: 'override',
                     text: 'I understand that overriding does not make the assumption hold, and '
                           + 'that the report will state that this analysis proceeded without it.',

                     // `AC-UI-19`: BOTH a non-empty reason and the tick. The report prints the
                     // reason, so an empty one would print an override nobody explained.
                     requiresReason: true,
                   }}>
          <AssumptionOverride payload={payload} />
        </GateShell>
      )
    }

    case 'sign':
      return shell(<Sign payload={payload} />, declared(payload, [
        opt('sign', 'Sign', 'IRREVERSIBLE. Your name, object id and the server time are recorded.'),
        opt('return', 'Send it back', 'Names the phase to re-enter; nothing is signed.'),
      ]))

    default:
      // A GATE KIND THIS BUILD DOES NOT KNOW. It is rendered with its payload rather than hidden:
      // a question nobody can see is a question nobody can answer, and the run waits forever.
      return shell(<Unknown payload={payload} />, declared(payload, []))
  }
}

/**
 * THE OPTIONS THE HARNESS DECLARED, where it declared them.
 *
 * The payload carries its own `options` — an unregistered collision, for instance, offers
 * `intentional` and `error` and NOT `suppress`. Falling back to this card's own list would offer a
 * verdict the harness refuses, so a declared list always wins and the labels are matched to it.
 */
function declared(payload: Record<string, unknown>,
                  fallback: readonly GateOption[]): readonly GateOption[] {
  const offered = payload['options']
  if (!Array.isArray(offered) || offered.length === 0) return fallback

  return offered.filter((o): o is string => typeof o === 'string').map((value) => {
    const known = fallback.find((o) => o.value === value)
    return known ?? opt(value, humanise(value), 'Recorded as your decision.')
  })
}

function opt(value: string, label: string, consequence: string): GateOption {
  return { value, label, consequence }
}

/**
 * A verdict this build has no label for, rendered readably rather than hidden.
 *
 * A harness that adds a verdict must not become a card with a missing button: the run would wait
 * on a decision nobody could give.
 */
function humanise(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}


/**
 * THE BINDING CARD, which is the one that carries the whole evidence vocabulary.
 *
 * <p>It is its own component because two of `§5.2`'s MUSTs are STATEFUL and the others are not:
 * <i>Supply my own…</i> opens free text AND the upload control, and a <c>not-adequate</c> binding
 * needs a separate explicit acknowledgement before Accept enables. Both depend on which option is
 * selected, which the shell owns — so the shell is told, rather than the card guessing.</p>
 */
function BindingCard({ payload, frame, busy, answer, options, onAnswer }: {
  payload: Record<string, unknown>
  frame: Frame
  busy: boolean
  answer: GateAnswered | null
  options: readonly GateOption[]
  onAnswer: (answer: GateAnswer) => void
}): React.JSX.Element {

  // `§5.2`: A `not-adequate` BINDING — route ⑥, an explicit estimate — requires a separate
  // acknowledgement before Accept enables. It is the weakest grade this system will bind, and
  // approving one on a radio button alone records a click where the specification wants a decision.
  const unsourced = str(payload['grade']) === 'not-adequate'

  return (
    <GateShell kind="approve_binding"
               origin={str(frame['origin']) === 'agent' ? 'agent' : 'harness'}
               state={str(frame['state']) ?? 'Awaiting you'}
               heading={HEADINGS['approve_binding'] ?? ''}
               options={options} busy={busy} answer={answer} onAnswer={onAnswer}
               from={agentFor(num(frame['phase'])).name}
               acknowledge={unsourced
                 ? { option: 'approve',

                     // `§5.2`'s own words, verbatim. The tick alone: the grade and the weaknesses
                     // list already say what is wrong with the value, and there is nothing further
                     // for the reader to explain.
                     text: 'I acknowledge this is an unsourced estimate.',
                     requiresReason: false }
                 : null}>
      {/* "SUPPLY MY OWN…" opens the shell's own free-text field, which every card carries. The
          DOCUMENT UPLOAD that `§5.2a` puts beside it is OUT OF SCOPE FOR v1 by the product
          owner's decision — see `Upload.tsx`, which is retained and unreferenced. */}
      <Binding payload={payload} />
    </GateShell>
  )
}

// ---- the ten bodies ---------------------------------------------------------------------------

function Binding({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const weaknesses = array(payload['weaknesses'])
  const range = payload['range']

  return (
    <>
      <p className="lead">
        <strong>{str(payload['parameter'])}</strong>
      </p>
      {/* GRADE AND PROVENANCE DIFFER IN SHAPE, NOT ONLY COLOUR — `AC-UI-5`, and `§5.2`: "the
          central point of the whole design is that WHERE a value came from and HOW GOOD it is are
          independent." `eia-brand.css` gives provenance a dashed outlined pill and grade a filled
          rectangle, so the distinction survives greyscale and colour-blindness. */}
      <div className="sect" style={{ display: 'flex', gap: '9px', flexWrap: 'wrap',
                                     alignItems: 'center' }}>
        <span className="chip prov">{str(payload['source']) !== null ? 'search-proposed' : 'user'}</span>
        {str(payload['grade']) !== null && (
          <span className={`chip grade ${str(payload['grade'])}`}>{str(payload['grade'])}</span>
        )}
        <span className="note">provenance is an outlined pill · grade is a filled block</span>
      </div>

      <dl className="meta">
        <Row label="Value" value={json(payload['values'])} />
        <Row label="Where it came from" value={str(payload['source']) ?? str(payload['basis'])} />
        <Row label="How much it matters" value={str(payload['materiality'])} />

        {/* A RANGE, OR THE WORDS. Nine of thirteen in-scope parameters had no established range in
            the reference walkthrough, and saying so is the honest output. */}
        <Row label="Range"
             value={range && Object.keys(range as object).length > 0
               ? json(range) : 'no established range'} />
        <Row label="Questions asked to get here" value={str(payload['conversation_turns'])} />
        {str(payload['geography']) && (
          <Row label="Measured elsewhere" value={str(payload['geography'])} />
        )}
      </dl>

      {/* AT LEAST ONE WEAKNESS. A value with no stated weakness has not been examined, and the
          schema already refuses one — this is where the reader sees what was found. */}
      <div className="sect">
        <p className="mlabel">Weaknesses: mandatory, at least one</p>
        <ul className="weak">
          {weaknesses.map((w, i) => <li key={i}>{typeof w === 'string' ? w : json(w)}</li>)}
        </ul>
      </div>
    </>
  )
}

function Capability({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const boundary = (payload['boundary'] ?? payload) as Record<string, unknown>
  const direction = str(payload['direction_of_error']) ?? str(boundary['direction_of_error'])

  return (
    <>
      {/* THE DIRECTION OF ERROR, PROMINENTLY. It is the one fact that changes how a reader should
          treat every figure that follows. */}
      {direction && (
        <p className="direction">
          This analysis <strong>{direction}</strong> the true effect.
        </p>
      )}

      <h4>Excluded by method, not by omission</h4>
      <ul>
        {array(payload['excluded_effects'] ?? boundary['excluded_effects'])
          .map((e, i) => <li key={i}>{typeof e === 'string' ? e : json(e)}</li>)}
      </ul>

      <dl className="meta">
        <Row label="A better method would be"
             value={str(payload['most_appropriate_alternative'])
               ?? str(boundary['most_appropriate_alternative'])} />
        <Row label="What would make it available"
             value={str(payload['route_forward']) ?? str(boundary['route_forward'])} />
      </dl>

      {/* AT PHASE 3, NOT AT PHASE 11. "If we net this, the answer will be a range" changes what a
          user asks for, and it is worthless after the work is done. */}
      {str(payload['publication_warning']) && (
        <p className="residual">{str(payload['publication_warning'])}</p>
      )}
    </>
  )
}

function Ratify({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const rejected = array(payload['alternatives_rejected'])
  const singleton = payload['singleton'] === true

  return (
    <>
      <p className="lead">{str(payload['approach'])}</p>

      {/* THE REJECTED-ALTERNATIVES TABLE IS MANDATORY — or the singleton branch, which says there
          were none and gives the counts. "Do not manufacture alternatives to reject." */}
      {rejected.length > 0 ? (
        <div className="sect">
          <p className="mlabel">Considered and rejected</p>
          <ul className="rejected">
            {rejected.map((r, i) => (
              <li key={i}>
                <b>{str(r['id'])}</b>
                <span>{str(r['disqualifying_condition'])}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : singleton ? (
        <p className="singleton">
          Only one method qualified. Nothing was rejected, and the funnel below is why.
        </p>
      ) : (
        // NEITHER BRANCH, WHICH IS ITSELF THE FINDING (`AC-UI-16`). `§6.3` MUSTs the rejected
        // table or the singleton branch; a card that has neither has not justified anything, and
        // rendering an empty table with two column headings under it says the opposite — it looks
        // like a table that happens to be empty rather than a justification that is missing. A
        // live run produced exactly this, with `alternatives_rejected: []` and `singleton: false`.
        <p className="residual">
          <span className="g" aria-hidden="true">ⓘ</span>
          <span>
            <b>No rejected alternatives were listed, and this was not a singleton.</b> The
            justification for this approach is therefore untested against the options never
            considered, which is the one thing this card exists to show you.
          </span>
        </p>
      )}

      {payload['funnel'] != null && (
        <p className="funnel">Filter stages: {json(payload['funnel'])}</p>
      )}

      <dl className="meta">
        <Row label="Stated limitation" value={str(payload['stated_limitation'])} />
      </dl>

      {/* THE RESIDUAL-LIMITS BOX. The gate's own failure mode is rubber-stamping, and a card that
          reads as a formality will be treated as one. */}
      {/* THE TWO RESIDUAL LIMITS, mandatory on every RATIFY card. `§6.3`: "the gate's own failure
          mode is rubber-stamping, and a card that reads as a formality will be treated as one." */}
      <div className="sect">
        <p className="residual">
          <span className="g" aria-hidden="true">ⓘ</span>
          <span>
            {array(payload['residual_limits'])
              .map((l) => (typeof l === 'string' ? l : json(l))).join(' ')}
          </span>
        </p>
      </div>
    </>
  )
}

function Geography({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const items = array(payload['items'])

  return (
    <>
      <p className="lead">
        These values were measured in another economy and applied here.
      </p>
      <table className="table">
        <caption>Transferred parameters</caption>
        <thead>
          <tr>
            <th scope="col">Parameter</th><th scope="col">Measured in</th>
            <th scope="col">Value</th><th scope="col">Grade</th>
            <th scope="col">Accepting means</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i}>
              <td>{str(row['parameter'])}</td>
              <td>{str(row['source_geography'])}</td>
              <td>{json(row['value'])}</td>
              <td>{str(row['grade'])}</td>
              {/* §5.3d MUSTs every row state whether accepting places it in the sensitivity
                  scope. It always does — a transfer is one of §7.1's four disjuncts. */}
              <td>{row['enters_sensitivity_scope'] === false
                ? 'it is used as given'
                : 'it is used AND flexed in the sensitivity analysis'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function Suppression({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const pair = array(payload['method_pair'])
  const registered = payload['grounds_id'] != null

  return (
    <>
      <p className="lead">
        Two methods both claim the effect <strong>{str(payload['tag'])}</strong>.
      </p>
      <ul>{pair.map((m, i) => <li key={i}>{typeof m === 'string' ? m : json(m)}</li>)}</ul>

      <p>
        Counting both would count the same activity twice. Counting neither would drop it.
      </p>

      {/* AN UNREGISTERED COLLISION CANNOT BE SUPPRESSED, and the card says why rather than simply
          omitting the option — a missing button with no explanation reads as a bug. */}
      {registered
        ? <dl className="meta"><Row label="Registered grounds" value={str(payload['grounds_id'])} /></dl>
        : (
          <p className="residual">
            No grounds are registered for this pair, so suppression is not offered. A double-count
            nobody has written down grounds for is not one this system will help you dismiss.
          </p>
        )}
    </>
  )
}

function Conversion({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  return (
    <>
      <p className="lead">
        One step's output must be converted before the next can use it.
      </p>
      <dl className="meta">
        <Row label="Conversion" value={str(payload['id'])} />
        <Row label="Factor" value={json(payload['factor'])} />
        <Row label="From" value={str(payload['from_unit']) ?? str(payload['from'])} />
        <Row label="To" value={str(payload['to_unit']) ?? str(payload['to'])} />
        <Row label="Where it applies" value={str(payload['scope']) ?? str(payload['edge'])} />
      </dl>
    </>
  )
}

function Framing({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const conflict = payload['horizon_conflict'] as Record<string, unknown> | null | undefined
  const greenBook = (str(payload['text']) ?? '') !== ''

  return (
    <>
      {/* THE GREEN BOOK TEXT VERBATIM — IN ITS WORDS, NOT IN ITS LINE BREAKS. It is doc 06 §7.5's
          own wording and paraphrasing it would change a framing decision into a summary of one.
          It arrives hard-wrapped at the source document's 100 columns and marked up with `**`/`*`;
          in a `<pre>` those columns cannot fit the card and ran past its right edge (B-221). */}
      {greenBook && <Prose text={str(payload['text']) ?? ''} />}

      {conflict && (
        <div className="gate__conflict">
          {/* Alone on the card, the card's own heading already names it. */}
          {greenBook && <h4>Two different horizons</h4>}
          <p>{str(conflict['concepts'])}</p>
        </div>
      )}
    </>
  )
}

/**
 * PROSE FROM TEXT WRAPPED FOR A DOCUMENT, not for a card. Blank lines separate paragraphs; a single
 * line break inside a paragraph is the source's column wrap and becomes a space. The one exception is
 * a leading `⚠` line, which is a heading and keeps its own line rather than running into the sentence
 * after it. Not a word is added, dropped or reordered.
 */
function proseParagraphs(text: string): string[][] {
  return text
    .split(/\n[ \t]*\n/)
    .map((block) => {
      const lines: string[] = []
      for (const raw of block.split('\n')) {
        const line = raw.trim()
        if (line.length === 0) continue
        const previous = lines[lines.length - 1]
        if (previous === undefined || previous.startsWith('⚠')) lines.push(line)
        else lines[lines.length - 1] = `${previous} ${line}`
      }
      return lines
    })
    .filter((lines) => lines.length > 0)
}

/**
 * `**strong**` AND `*emphasis*` AS ELEMENTS, NEVER AS HTML. Everything else is a text node, which React
 * escapes, so a payload cannot inject markup through this path.
 */
function emphasis(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  const marks = /\*\*([^*]+?)\*\*|\*([^*\s][^*]*?)\*/g
  let last = 0
  let key = 0
  for (let m = marks.exec(text); m !== null; m = marks.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index))
    parts.push(m[1] !== undefined
      ? <strong key={key++}>{m[1]}</strong>
      : <em key={key++}>{m[2]}</em>)
    last = marks.lastIndex
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

function Prose({ text }: { text: string }): React.JSX.Element {
  return (
    <div className="gate__verbatim">
      {proseParagraphs(text).map((lines, i) => (
        <p key={i}>
          {lines.map((line, j) => (
            <Fragment key={j}>
              {j > 0 && <br />}
              {emphasis(line)}
            </Fragment>
          ))}
        </p>
      ))}
    </div>
  )
}

function AssumptionOverride({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  const violations = array(payload['violations'])

  return (
    <>
      <table className="table">
        <caption>Assumptions this analysis does not satisfy</caption>
        <thead>
          <tr>
            <th scope="col">Assumption</th><th scope="col">What it says</th>
            <th scope="col">Severity</th>
          </tr>
        </thead>
        <tbody>
          {violations.map((v, i) => (
            <tr key={i}>
              <td>{str(v['id'])}</td>
              <td>{str(v['statement']) ?? str(v['text'])}</td>
              <td>{str(v['severity'])}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* WHAT OVERRIDING ADMITS. Not what it permits — what it concedes. */}
      <p className="residual">
        Overriding does not make the assumption hold. It records that you proceeded knowing it does
        not, and the report says so.
      </p>
    </>
  )
}

function Sign({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  return (
    <>
      <p className="irreversible">This cannot be undone.</p>

      <dl className="meta">
        <Row label="Run" value={str(payload['run_id'])} />
        <Row label="Specification version" value={str(payload['spec_version'])} />
        {/* THE TWO ARTEFACTS TOGETHER WITH THE SPECIFICATION sha256. A signature over a report
            whose specification is not identified signs a document, not an analysis. */}
        <Row label="Specification sha256" value={str(payload['sha256'])} mono />
        <Row label="Published as" value={str(payload['publication_form'])} />
        <Row label="Weakest grade on a headline" value={str(payload['weakest_headline_grade'])} />
        <Row label="Open flags" value={str(payload['open_flag_count'])} />
      </dl>

      {payload['point_estimate_permitted'] === false && (
        <p className="residual">
          This analysis reports a range rather than a point estimate. That is a property of the
          evidence, and signing does not change it.
        </p>
      )}

      {/* THE FREE-TEXT FIELD IS "SIGNING ON BEHALF OF", NEVER THE SIGNATORY. The signatory is the
          token's object id, UPN, display name and the server's clock (AC-AUTH-6). */}
      <p className="note">
        If you are signing on someone else's behalf, say so below. It is recorded as a note — the
        signatory is you.
      </p>
    </>
  )
}

function Unknown({ payload }: { payload: Record<string, unknown> }): React.JSX.Element {
  return (
    <>
      <p className="residual">
        This question needs an answer and this page does not recognise its type. Everything the
        system asked is below.
      </p>
      <pre className="verbatim">{json(payload)}</pre>
    </>
  )
}

// ---- small helpers ----------------------------------------------------------------------------

function Row({ label, value, chip, mono }:
             { label: string; value: string | null; chip?: boolean; mono?: boolean })
  : React.JSX.Element | null {
  if (value === null || value === '') return null

  return (
    <div className="gate__row">
      <dt>{label}</dt>
      <dd className={[chip ? 'chip' : '', mono ? 'mono' : ''].filter(Boolean).join(' ')}>{value}</dd>
    </div>
  )
}

function num(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

function str(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

function array(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value as Record<string, unknown>[] : []
}

function json(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}
